import axios from "axios";
import { defineStore } from "pinia";
import { reactive, ref } from "vue";
import { useGlobalMessagesStore } from "./globalMessagesStore";
import { Audit, AuditDocument, AuditEdit, PagedList } from "@/audit.types";
import { BTableOptions, serializeSortBy } from "@/components/BTable/BTable.types";

import { handleError } from "./storeHelper";
import { BFieldSelection, BFilterableField, BFilterOptions, dateTimeOperations, listOperations, textOperations } from "@/components/BFilterEditor/BFilterEditor.types";
import { useOriStore } from "./oriStore";
import { serializeFilters } from "@/components/BTable/HelperFunctions";
const globalMessages = useGlobalMessagesStore();
const oriStore = useOriStore();

export const useAuditStore = defineStore("auditStore", () => {
    const apiUrl = 'api/v1/audit/audits';
    const loading = ref<boolean>(false);
    const items = ref<Audit[]>();


    const filterOptions = ref<BFilterOptions>({
        filters: [],
    });
    const tableOptions = ref<BTableOptions>({
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [],
        filterBy: [],
        pagingMode: "withTotal"
    } as BTableOptions);


    const statuses: BFieldSelection[] = [
        { text: "New", value: "AN" },
        { text: "In Progress", value: "AP" },
        { text: "Closed", value: "AC" }];

    const filterableFields = reactive<BFilterableField[]>([
        {
            property: "auditName",
            label: "Audit Name",
            operations: textOperations,
            editorComponent: "text-editor",
        },
        {
            property: "agency",
            label: "Agency",
            operations: listOperations,
            editorComponent: "list-editor",
            editorOptions: oriStore.getOrisAsBFieldSelectionArray,
        },
        {
            property: "startDate",
            label: "Start Date",
            operations: dateTimeOperations,
            editorComponent: "date-editor"
        },
        {
            property: "status",
            label: "Status",
            operations: listOperations,
            editorComponent: "list-editor",
            editorOptions: statuses
        },
    ]);

    const getCurrentUser = async () => {

        try {
            loading.value = true;
            const response = await axios.get(`${apiUrl}/current-user`);
            return response.data;

        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }
    const list = async () => {

        try {
            loading.value = true;

            let filterParam = serializeFilters(filterOptions.value.filters, filterableFields);
            let sortParam = serializeSortBy(tableOptions.value.sortBy);

            const response = await axios.get<PagedList<Audit>>(apiUrl, {
                params: {
                    limit: tableOptions.value.itemsPerPage,
                    offset: tableOptions.value.itemsPerPage * (tableOptions.value.page - 1),
                    sort: sortParam,
                    filter: filterParam,
                }
            });
            items.value = response.data.data;
            tableOptions.value.totalItemCount = response.data.total;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const create = async (audit: Audit): Promise<Audit> => {
        try {
            loading.value = true;
            const response = await axios.put<Audit>(apiUrl, audit);
            globalMessages.push({ message: "Audit created successfully.", type: "success" })
            return response.data;
        }
        catch (error) {
            handleError(error)
            throw error;
        }
        finally {
            loading.value = false;
        }
    }
    const close = async (auditId: number): Promise<Audit> => {
        try {
            loading.value = true;
            const response = await axios.post<Audit>(`${apiUrl}/actions/${auditId}/close`);
            globalMessages.push({ message: "Audit closed successfully.", type: "success" })
            return response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const getById = async (id: number): Promise<Audit> => {

        try {
            loading.value = true;
            const response = await axios.get<Audit>(`${apiUrl}/${id}`);
            return response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const upload = async (id: number, file: File): Promise<void> => {
        try {
            loading.value = true;
            let formData = new FormData();
            formData.append('file', file);
            const response = await axios.post<AuditDocument>(`${apiUrl}/actions/${id}/upload`, formData);
            globalMessages.push({ message: "Report uploaded successfully.", type: "success" })
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const update = async (id: number, audit: AuditEdit): Promise<AuditEdit> => {

        try {
            loading.value = true;
            const response = await axios.post<AuditEdit>(`${apiUrl}/${id}`, audit);
            globalMessages.push({ message: "Audit updated successfully.", type: "success" })
            return response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    return {
        tableOptions,
        filterOptions,
        filterableFields,
        loading,
        items,
        getCurrentUser,
        close,
        create,
        update,
        getById,
        upload,
        list
    };
})